import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllFeeConfigurations,
  getFeeConfiguration,
  createFeeConfiguration,
  updateFeeConfiguration,
  deleteFeeConfiguration,
  importFeeConfigurations,
  getAllHudLines,
  getHudLine,
  createHudLine,
  updateHudLine,
  deleteHudLine,
  importHudLines,
  getAllVersions,
  getVersion,
  publishVersion,
  deleteVersion,
} from '../services/feeService';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRulesVersionViewModel,
  CreateOrUpdateFeeConfigRequestViewModel,
  CreateOrUpdateFeeHudLineViewModel,
  PublishFeeRulesVersionRequestViewModel,
} from '../models/fees';

// ===== Fee Configurations =====

export const useFeeConfigurations = () => {
  const queryClient = useQueryClient();

  const feeConfigurationsQuery = useQuery<FeeConfigurationViewModel[]>({
    queryKey: ['fee-configurations'],
    queryFn: () => getAllFeeConfigurations(),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateFeeConfigRequestViewModel) => createFeeConfiguration(payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      await queryClient.refetchQueries({ queryKey: ['fee-configurations'] });
      queryClient.setQueryData(['fee-configuration', created.feeConfigurationId], created);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateOrUpdateFeeConfigRequestViewModel }) =>
      updateFeeConfiguration(id, payload),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      await queryClient.refetchQueries({ queryKey: ['fee-configurations'] });
      queryClient.setQueryData(['fee-configuration', updated.feeConfigurationId], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteFeeConfiguration(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      queryClient.removeQueries({ queryKey: ['fee-configuration', id] });
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importFeeConfigurations(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
    },
  });

  return {
    feeConfigurationsQuery,
    createFeeConfiguration: createMutation.mutateAsync,
    updateFeeConfiguration: updateMutation.mutateAsync,
    deleteFeeConfiguration: deleteMutation.mutateAsync,
    importFeeConfigurations: importMutation.mutateAsync,
  };
};

export const useFeeConfiguration = (id?: number) => {
  const queryClient = useQueryClient();

  const feeConfigurationQuery = useQuery<FeeConfigurationViewModel>({
    queryKey: ['fee-configuration', id],
    queryFn: () => getFeeConfiguration(id!),
    enabled: !!id,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateFeeConfigRequestViewModel) => {
      if (!id) throw new Error('Fee configuration ID is required');
      return updateFeeConfiguration(id, payload);
    },
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      await queryClient.refetchQueries({ queryKey: ['fee-configurations'] });
      queryClient.setQueryData(['fee-configuration', updated.feeConfigurationId], updated);
    },
  });

  return {
    feeConfigurationQuery,
    updateFeeConfiguration: updateMutation.mutateAsync,
  };
};

// ===== HUD Lines =====

export const useHudLines = (feeRulesVersionId?: number | null) => {
  const queryClient = useQueryClient();

  const hudLinesQuery = useQuery<FeeHudLineViewModel[]>({
    queryKey: ['hud-lines', feeRulesVersionId],
    queryFn: () => getAllHudLines(feeRulesVersionId),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateFeeHudLineViewModel) => createHudLine(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
      queryClient.setQueryData(['hud-line', created.feeHudLineId], created);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateOrUpdateFeeHudLineViewModel }) =>
      updateHudLine(id, payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
      queryClient.setQueryData(['hud-line', updated.feeHudLineId], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteHudLine(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
      queryClient.removeQueries({ queryKey: ['hud-line', id] });
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importHudLines(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
    },
  });

  return {
    hudLinesQuery,
    createHudLine: createMutation.mutateAsync,
    updateHudLine: updateMutation.mutateAsync,
    deleteHudLine: deleteMutation.mutateAsync,
    importHudLines: importMutation.mutateAsync,
  };
};

export const useHudLine = (id?: number) => {
  const queryClient = useQueryClient();

  const hudLineQuery = useQuery<FeeHudLineViewModel>({
    queryKey: ['hud-line', id],
    queryFn: () => getHudLine(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateFeeHudLineViewModel) => {
      if (!id) throw new Error('HUD Line ID is required');
      return updateHudLine(id, payload);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
      queryClient.setQueryData(['hud-line', updated.feeHudLineId], updated);
    },
  });

  return {
    hudLineQuery,
    updateHudLine: updateMutation.mutateAsync,
  };
};

// ===== Versions =====

export const useFeeVersions = () => {
  const queryClient = useQueryClient();

  const versionsQuery = useQuery<FeeRulesVersionViewModel[]>({
    queryKey: ['fee-versions'],
    queryFn: () => getAllVersions(),
    staleTime: 30_000,
  });

  const publishMutation = useMutation({
    mutationFn: (payload: PublishFeeRulesVersionRequestViewModel) => publishVersion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-versions'] });
      queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-versions'] });
      queryClient.invalidateQueries({ queryKey: ['fee-configurations'] });
      queryClient.invalidateQueries({ queryKey: ['hud-lines'] });
    },
  });

  return {
    versionsQuery,
    publishVersion: publishMutation.mutateAsync,
    deleteVersion: deleteMutation.mutateAsync,
  };
};

export const useFeeVersion = (id?: number) => {
  const versionQuery = useQuery<FeeRulesVersionViewModel>({
    queryKey: ['fee-version', id],
    queryFn: () => getVersion(id!),
    enabled: !!id,
  });

  return {
    versionQuery,
  };
};

// ===== Combined Hook (for convenience) =====

export const useFees = (configId?: number) => {
  const feeConfigurations = useFeeConfigurations();
  const feeConfiguration = useFeeConfiguration(configId);
  const hudLines = useHudLines();
  const versions = useFeeVersions();

  return {
    ...feeConfigurations,
    ...feeConfiguration,
    ...hudLines,
    ...versions,
  };
};

